<template>
  <section
    :class="[
      $style['hero'],
      {[$style['hero--height-auto']]: heightAuto},
      {[$style['hero--align-center']]: alignCenter},
      {[$style['hero--no-overlay']]: noOverlay},
      {[$style['hero--long-title']]: titleLength > 22}
    ]"
    :style="{ '--fit': Math.max(longestWord, 6) }">
    <div :class="$style['hero__background']">
      <slot name="background" />
    </div>
    <div :class="$style['hero__curtain']" aria-hidden="true" />
    <div :class="$style['hero__container']">
      <div v-if="!!$slots.default" :class="$style['hero__content']">
        <slot />
      </div>
      <menu v-if="!!$slots.actions" :class="$style['hero__actions']">
        <slot name="actions" />
      </menu>
    </div>
  </section>
</template>
<script lang="ts" setup>
// A photo behind a velvet curtain that parts when the page opens, with the title in giant capitals.
// titleLength and longestWord (in characters) let the title fill the width without breaking words.
interface Props {
  heightAuto?: boolean
  noOverlay?: boolean
  alignCenter?: boolean
  titleLength?: number
  longestWord?: number
}
withDefaults(defineProps<Props>(), {
  heightAuto: false,
  noOverlay: false,
  alignCenter: false,
  titleLength: 0,
  longestWord: 10
})
</script>
<style module>
.hero {
  --text-base: var(--stage-white);
  --title-max: var(--stage-title);

  position: relative;
  isolation: isolate;
  overflow: hidden;
  display: flex;
  align-items: flex-end;
  width: 100%;

  /* Low enough that the first lines of the page show under it */
  min-height: min(70svh, 44rem);
  background: var(--stage-black);
  color: var(--stage-white);
  container: hero / inline-size;
}

.hero--long-title {
  --title-max: var(--stage-title-long);
}

.hero__background {
  position: absolute;
  inset: 0;
  z-index: -2;
  overflow: hidden;
  animation: hero-settle 2.4s var(--stage-ease) both;
}

.hero__background img,
.hero__background video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: 50% 30%;
}

/* Fades the photo into the black page, and keeps the header readable */
.hero:not(.hero--no-overlay)::after {
  content: "";
  position: absolute;
  inset: 0;
  z-index: -1;
  pointer-events: none;
  background:
    linear-gradient(oklch(0% 0 0deg / 45%), oklch(0% 0 0deg / 0%) 25%),
    linear-gradient(0deg, var(--stage-black) 4%, oklch(0% 0 0deg / 70%) 30%, oklch(0% 0 0deg / 0%) 65%);
}

/* Two velvet drapes that part as the page opens */
.hero__curtain {
  position: absolute;
  inset: 0;
  z-index: 3;
  display: grid;
  grid-template-columns: 1fr 1fr;
  pointer-events: none;
}

.hero__curtain::before,
.hero__curtain::after {
  content: "";
  background:
    linear-gradient(90deg, oklch(0% 0 0deg / 0%) 70%, oklch(0% 0 0deg / 45%)),
    repeating-linear-gradient(
      90deg,
      var(--velours-1) 0,
      var(--velours-2) 1.3rem,
      var(--velours-3) 2.4rem,
      var(--velours-2) 3.5rem,
      var(--velours-1) 4.8rem
    );
  animation: hero-part-left 1.3s var(--stage-ease) 0.15s both;
}

.hero__curtain::after {
  transform: scaleX(-1);
  animation-name: hero-part-right;
}

@keyframes hero-part-left {
  to { transform: translateX(-115%) skewX(-4deg); }
}

@keyframes hero-part-right {
  from { transform: scaleX(-1); }
  to { transform: translateX(115%) scaleX(-1) skewX(-4deg); }
}

@keyframes hero-settle {
  from { transform: scale(1.12); }
}

@keyframes hero-rise {
  from {
    opacity: 0;
    transform: translateY(0.35em);
  }
}

.hero__container {
  width: 100%;
  max-width: var(--container-width);
  margin: 0 auto;
  padding: calc(var(--topbar-height) + var(--spacing-xxl)) var(--stage-gutter) clamp(2rem, 1rem + 4vw, 4.5rem);
}

.hero__content {
  max-width: 100%;
  line-height: 1.45;
  text-wrap: pretty;
}

.hero__content > * {
  animation: hero-rise 900ms var(--stage-ease) 0.75s both;
}

.hero__content p {
  max-width: 38rem;
  margin: var(--spacing-xl) 0 0;
  font-size: clamp(1.125rem, 1rem + 0.5vw, 1.5rem);
  color: var(--stage-soft);
}

.hero__content :is(h1, h2, h3) {
  max-width: none;
  margin: 0;
  color: var(--stage-white);
  font-size:
    min(
      var(--title-max),
      (min(100cqi, var(--container-width)) - 2 * var(--stage-gutter)) / (var(--fit) * 0.72)
    );
  line-height: 0.86;
  letter-spacing: -0.04em;
}

/* In Storyblok: bold words stay solid, the rest of the title is drawn as an outline */
.hero__content :is(h1, h2, h3):has(b, strong) {
  color: transparent;
  -webkit-text-stroke: 0.025em var(--stage-white);
}

.hero__content :is(h1, h2, h3) :is(b, strong) {
  color: var(--stage-white);
  -webkit-text-stroke: 0;
}

.hero__actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-l);
  margin: var(--spacing-xxl) 0 0;
  padding: 0;
  animation: hero-rise 900ms var(--stage-ease) 0.9s both;
}

.hero--height-auto {
  min-height: 0;
  aspect-ratio: 16 / 9;
}

.hero--align-center .hero__container {
  text-align: center;
}

.hero--align-center .hero__content p {
  margin-inline: auto;
}

.hero--align-center .hero__actions {
  justify-content: center;
}

@media (width <= 50rem) {
  .hero {
    min-height: 72svh;
  }

  .hero--height-auto {
    aspect-ratio: 9 / 16;
  }
}

@media (prefers-reduced-motion: reduce) {
  .hero__background,
  .hero__content > *,
  .hero__actions {
    animation: none;
  }

  .hero__curtain {
    display: none;
  }
}
</style>
